import type React from "react"
import { Trash2, User, Mail, Phone } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import { Switch } from "../ui/switch"
import { Label } from "../ui/label"
import { Tooltip } from "../ui/tooltip"
import { isPhase2DemoEnabled } from "../../demo/phase2Demo"

interface Attorney {
  id: string
  name: string
  email: string
  phone: string
}

interface AttorneyCardProps {
  attorney: Attorney
  onSelect: (attorneyId: string) => void
  onDelete: (attorneyId: string) => void
  availableForLeads?: boolean
  onAvailabilityChange?: (attorneyId: string, available: boolean) => void
}

export default function AttorneyCard({
  attorney,
  onSelect,
  onDelete,
  availableForLeads = true,
  onAvailabilityChange,
}: AttorneyCardProps) {
  const showAvailability = isPhase2DemoEnabled() && onAvailabilityChange

  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-2">
        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {/* Name */}
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0 w-32 lg:w-40">
              <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <h3
                className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                onClick={() => onSelect(attorney.id)}
              >
                {attorney.name}
              </h3>
            </div>

            {/* Email */}
            <div className="flex items-center gap-2 flex-shrink-0 w-48 lg:w-56">
              <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <a href={`mailto:${attorney.email}`} className="text-sm text-slate-700 hover:text-slate-900 transition-colors truncate">
                {attorney.email}
              </a>
            </div>

            {/* Phone */}
            <div className="flex items-center gap-2 flex-shrink-0 w-36 lg:w-44">
              <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-700 truncate">
                +{attorney.phone}
              </span>
            </div>
          </div>

          {showAvailability && (
            <Tooltip content="When off, new leads are not routed to this attorney (demo)." side="top">
              <div className="flex items-center gap-2 flex-shrink-0">
                <Label className="text-xs text-slate-600 whitespace-nowrap">Available</Label>
                <Switch
                  checked={availableForLeads}
                  onChange={(e) => onAvailabilityChange(attorney.id, e.target.checked)}
                />
              </div>
            </Tooltip>
          )}

          {/* Delete Button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0"
            onClick={() => onDelete(attorney.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="flex justify-between items-start mb-3">
            {/* Name and Contact Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <h3
                  className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                  onClick={() => onSelect(attorney.id)}
                >
                  {attorney.name}
                </h3>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <a
                  href={`mailto:${attorney.email}`}
                  className="text-sm text-slate-700 hover:text-slate-900 transition-colors"
                >
                  {attorney.email}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">
                  +{attorney.phone}
                </span>
              </div>
            </div>

            {/* Delete Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              onClick={() => onDelete(attorney.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {showAvailability && (
            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              <Label className="text-xs text-slate-600">Available for leads</Label>
              <Switch
                checked={availableForLeads}
                onChange={(e) => onAvailabilityChange(attorney.id, e.target.checked)}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
} 